import type { ContainerVanChecklistData } from '../types/containerVan';

interface ContainerVanChecklistCategory {
  id: string;
  name: string;
  step_label: string;
  color: string;
  order: number;
  items: ContainerVanChecklistItem[];
}

interface ContainerVanChecklistItem {
  id: string;
  text: string;
  order: number;
  required: boolean;
  type?: 'text' | 'checkbox' | 'slider' | 'textarea' | 'gauge';
  icon?: string;
  value?: any;
  notes?: string;
  photos?: string[];
  unit?: string;
}

/**
 * Format timestamp to date and time strings
 */
const formatTimestamp = (timestamp: any): { date: string; time: string } => {
  if (!timestamp) return { date: 'N/A', time: 'N/A' };
  
  let date: Date;
  if (timestamp.seconds) {
    // Firestore Timestamp
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    // Firestore Timestamp object
    date = timestamp.toDate();
  } else {
    // Regular Date
    date = new Date(timestamp);
  }
  
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  return { date: dateStr, time: timeStr };
};

/**
 * Find value by item ID (robust approach using actual item IDs)
 */
const findValueByItemId = (
  categories: ContainerVanChecklistCategory[] | undefined,
  itemId: string
): string => {
  try {
    if (!categories || categories.length === 0) {
      return 'N/A';
    }
    
    for (const category of categories) {
      if (category.items) {
        for (const item of category.items) {
          if (item.id === itemId) {
            let value: any = item.value;
            
            // Add unit if present
            if (item.unit) {
              value = `${value}${item.unit}`;
            }
            
            // Concatenate notes if present
            if (item.notes) {
              value = `${value} (Notes: ${item.notes})`;
            }
            
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'number') return value.toString();
            return String(value);
          }
        }
      }
    }
    
    return 'N/A';
  } catch (error) {
    console.error('Error in findValueByItemId:', error);
    return 'N/A';
  }
};

/**
 * Export container van checklist search results to PDF
 */
export const exportChecklistResultsToPDF = async (
  checklists: ContainerVanChecklistData[],
  companyName: string,
  searchFilters?: {
    createdFrom?: string;
    createdTo?: string;
    vanNumber?: string;
  }
): Promise<void> => {
  try {
    if (checklists.length === 0) {
      throw new Error('No checklists to export');
    }

    // Dynamic imports for better code splitting
    const [{ jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);

    // Create PDF
    const doc = new jsPDF('landscape', 'mm', 'a3');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Container Van Inspection Report', 15, 25);
    
    if (companyName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Company: ${companyName}`, 15, 35);
    }
    if (searchFilters) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Filters From: ${searchFilters.createdFrom} | To: ${searchFilters.createdTo}`, 15, 42);
    }
    // Add generation date
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 15, 49);

    // Define table headers (same as admin)
    const headers = [
      'Date Inspected',
      'Time Inspected', 
      'Location',
      'Container Van Number',
      'Refrigerated Van Temp.',
      'GenSet Number',
      'GenSet Remaining Fuel',
      'GenSet Remaining Operating Hours',
      'GenSet Running Time',
      'Inspection Summary',
      'Overall Status',
      'Inspected By'
    ];

    // Prepare table data
    const tableData = checklists.map(checklist => {
      const { date, time } = formatTimestamp(checklist.created_at);
      const vanTemp = findValueByItemId(checklist.categories, 'temperature_degrees_celsius');
      const gensetFuel = findValueByItemId(checklist.categories, 'remainin_fuel_liters');
      const gensetRunningTime = findValueByItemId(checklist.categories, 'hour_meter_reading');
      
      return [
        date, // Date Inspected
        time, // Time Inspected
        checklist.location || 'N/A', // Location
        checklist.vanNumber, // Container Van Number
        vanTemp, // Refrigerated Van Temp
        checklist.genSetNumber, // GenSet Number
        gensetFuel, // GenSet Remaining Fuel
        checklist.genSetRemainingHours ? checklist.genSetRemainingHours.toString() : 'N/A', // GenSet Remaining Operating Hours
        gensetRunningTime, // GenSet Running Time
        checklist.inspectionSummary || 'N/A', // Inspection Summary
        checklist.overallStatus.toUpperCase(), // Overall Status
        checklist.created_by || 'N/A' // Inspected By
      ];
    });

    // Add table
    autoTable(doc, {
    head: [headers],
    body: tableData,
    startY: 55,
    theme: 'grid',
    styles: {
        fontSize: 8,
        cellPadding: 2,
    },
    headStyles: {
        fillColor: [56, 178, 172], // Teal color
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
    },
    alternateRowStyles: {
        fillColor: [247, 250, 252], // gray.50
    },
    didParseCell: (data) => {
    // Color coding for status column (index 10)
        if (data.column.index === 10) {
        const cellValue = data.cell.text[0];
        if (cellValue === 'PASS') {
            data.cell.styles.textColor = [34, 139, 34]; // Green
            data.cell.styles.fontStyle = 'bold';
        } else if (cellValue === 'WARNING') {
            data.cell.styles.textColor = [255, 140, 0]; // Orange
            data.cell.styles.fontStyle = 'bold';
        } else if (cellValue === 'FAIL') {
            data.cell.styles.textColor = [220, 20, 60]; // Red
            data.cell.styles.fontStyle = 'bold';
        }
        }
    },
    // columnStyles: {
    //   0: { cellWidth: 20 }, // Date
    //   1: { cellWidth: 20 }, // Time
    //   2: { cellWidth: 25 }, // Location
    //   3: { cellWidth: 25 }, // Van Number
    //   4: { cellWidth: 20 }, // Van Temp
    //   5: { cellWidth: 25 }, // GenSet Number
    //   6: { cellWidth: 20 }, // GenSet Fuel
    //   7: { cellWidth: 20 }, // GenSet Operating Hours
    //   8: { cellWidth: 20 }, // GenSet Runtime
    //   9: { cellWidth: 40 }, // Inspection Summary
    //   10: { cellWidth: 25 }, // Overall Status
    //   11: { cellWidth: 25 }, // Inspected By
    // },
    margin: { left: 10, right: 10 },
    pageBreak: 'auto',
    tableWidth: 'auto',
    });
    
    // Add footer with total count
    const finalY = (doc as any).lastAutoTable.finalY || 50;
    doc.setFontSize(10);
    doc.text(`Total Records: ${checklists.length}`, 15, finalY + 15);

    // Add footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    };
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const filename = `container-van-checklist-report-${timestamp}.pdf`;
    doc.save(filename);

  } catch (error) {
    console.error('Error exporting checklist results:', error);
    throw new Error(`Failed to export checklist results: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};